<template>
  <div
    v-if="hasSubMenu"
    ref="subMenuRef"
    :class="{ 'st-menu': true, primary, 'no-button': noButton }"
    :link="linkStyle"
  >
    <CmdTooltip
      v-if="!noButton"
      :table="tableName"
      :index="menuIndex"
      :tip="tooltip"
      :detail="detail"
    >
      <ElButton ref="buttonRef" :icon="menuIcon" @click="selectMenu">{{
        menuText
      }}</ElButton>
    </CmdTooltip>

    <ElSubMenu
      :index="index"
      :disabled="disabled"
      popper-class="st-sm-popper"
      @mouseenter="onMouseEnter"
    >
      <template v-if="noButton" #title>
        <ElIcon v-if="menuIcon"><component :is="menuIcon" /></ElIcon>
        {{ label }}
      </template>
      <slot />
    </ElSubMenu>
  </div>

  <CmdTooltip
    v-else
    :table="tableName"
    :index="index"
    :tip="tooltip"
    :detail="detail"
    :badge-text="badgeText"
    :badge-type="badgeType"
  >
    <ElMenuItem
      class="st-menu2"
      :primary="primaryStyle"
      :text="textStyle"
      :link="linkStyle"
      :has-icon="hasIcon"
      v-bind="$attrs"
      :index="menuIndex"
      :disabled="disabled"
    >
      <ElIcon v-if="menuIcon"><component :is="menuIcon" /></ElIcon>
      {{ menuText }}
    </ElMenuItem>
  </CmdTooltip>
</template>

<script setup lang="ts">
/**
 * ### 操作按钮组件
 * 配合 cmd-menu-item 及 cmd-sub-menu使用
 */
import type { Component } from 'vue';
import { ref, shallowRef, computed, readonly, useSlots, provide } from 'vue';

import {
  useDisabled,
  ElButton,
  ElIcon,
  ElSubMenu,
  ElMenuItem
} from 'element-plus';

import { isObject, isString } from 'lodash-es';
import { shareData, useShareData } from '../../core/SharedData';
import { getLayout, saveLayout } from '../../core/LayoutPersistent';
import CmdTooltip from './CmdTooltip.vue';

const props = defineProps<{
  /**
   * ### 操作按钮唯一key
   * 用以与句柄函数对象key关联
   */
  index: string;
  /**
   * ### 操作按钮名称
   */
  label: string;
  /**
   * ### 按钮图标
   */
  icon?: Component;
  /**
   * ### 操作按钮权限code
   */
  require?: string; // 权限code
  /**
   * ### 帮助描述文字, “列表配置”中显示
   */
  info?: string;
  /**
   * ### tooltip帮助, 不填使用info
   */
  help?: string;
  /**
   * ### tooltip帮助, 不填使用info
   * <warning> 优先级低于help
   */
  tip?: string;
  /**
   * ### 在tooltip中显示“详见”按钮 点击后弹窗该文案
   * <warning> 需配合 help/tip/info之一一起使用
   */
  detail?: boolean;
  /**
   * ### 按钮是否显示primary样式
   * 同element-plus button组件 primary 一致
   */
  primary?: boolean;
  /**
   * ### 没有边框的文本样式, 连续两项之间有个"|"线分隔
   * 同element-plus button组件 text
   */
  text?: boolean;
  /**
   * ### 按钮显示link样式
   * 同element-plus button组件 text
   */
  link?: boolean;
  /**
   * ### 按钮是否禁止点击
   */
  disabled?: boolean;
  /**
   * ### 去掉子菜单置顶逻辑，同时按钮本身默认不能点击
   */
  noButton?: boolean;
  /**
   * ### 默认是否隐藏
   */
  defaultHide?: boolean;
  /**
   * ### 徽章文案
   * 即按钮右上角小提示文案
   */
  badgeText?: string;
  /**
   * ### 徽章类型
   * 影响徽章文案样式
   */
  badgeType?: 'success' | 'warning' | 'info' | 'primary' | 'danger';
}>();

// 告知 cmd-menu-item 是否显示setDefault图标
provide('st-menu-no-button', props.noButton);

const primaryStyle = props.primary ? '' : undefined;
const textStyle = props.text ? '' : undefined;
const linkStyle = props.link ? '' : undefined;

// tooltip
const tooltip = ref(props.help || props.tip || props.info);
const detail = ref<boolean | undefined>(props.detail);

// TODO 升级为useFormDisabled()
const disabled = useDisabled();

////////////////////////////////////////////
// 有子菜单的情况
const menuIndex = ref(props.index);
const menuText = ref(props.label);
const menuIcon = shallowRef(props.icon);

const hasIcon = computed(() => {
  if (menuIcon.value) return '';
  return undefined;
});

const children = useSlots().default?.() || [];
const hasSubMenu = children.some((e) => isObject(e.type));

let selectMenu: () => void;
let onMouseEnter: () => void;

const buttonRef = ref();
// 菜单元素
const subMenuRef = ref();

const tableName = useShareData('table', 'name');

if (hasSubMenu) {
  let empty = true;

  const name = useShareData('table', 'name');

  // 读到保存的配置
  const persistIndex = getLayout(
    'cmdbar',
    ['menu', name, props.index],
    '',
    isString
  );

  shareData('cmdbar:menu', {
    report: (
      menuId: string,
      text: string,
      help: { tip?: string; detail?: boolean }
    ) => {
      if (empty || persistIndex == menuId) {
        empty = false;
        tooltip.value = help.tip;
        detail.value = help.detail;

        menuText.value = text;
        menuIndex.value = menuId;
      }
    },

    set: (
      menuId: string,
      text: string,
      help: { tip?: string; detail?: boolean }
    ) => {
      empty = false;
      tooltip.value = help.tip;
      detail.value = help.detail;

      menuText.value = text;
      menuIndex.value = menuId;

      // 保存配置
      saveLayout('cmdbar', ['menu', name, props.index], menuId);
    },

    current: readonly(menuIndex)
  });

  const cmdbarSelect = useShareData('cmdbar', 'select') as any;
  selectMenu = async function () {
    menuIndex.value && (await cmdbarSelect(menuIndex.value));
  };

  onMouseEnter = function () {
    let width = (buttonRef.value?.$el.offsetWidth ?? 0) as number;
    document.body.style.setProperty('--st-sm-popper-offset', -width / 2 + 'px');

    // 计算弹出窗宽度
    width = (subMenuRef.value?.offsetWidth ?? 0) as number;
    if (width < 100) width = 100;

    document.body.style.setProperty('--st-sm-popper-minwidth', width + 'px');
  };
}
</script>

<style lang="scss" scoped>
.st-menu {
  position: relative;
  display: flex;
  box-sizing: border-box;
  height: 32px;
  margin-right: 8px;
  box-shadow: none;
  border-radius: 2px;
  border: 1px solid #bbbfc4;
  color: #192865;

  --el-menu-icon-transform-open: none;
  --el-menu-base-level-padding: 16px;

  &:hover {
    border-color: #2f7ff4;
    color: #2f7ff4;
  }

  & .el-button {
    height: 30px;
    padding-left: 12px;
    padding-right: 12px;
    box-shadow: none;
    border: none;
    border-radius: 0;
    border-right: 1px solid #bbbfc4;
  }

  & .el-button:focus {
    color: unset;
    background-color: white;
  }

  &:hover .el-button {
    background-color: transparent;
    border-right: 1px solid #2f7ff4;
  }

  &.primary .el-button {
    background-color: #327de8;
    border-right: 1px solid #2864b9;
    color: white;
  }

  &.primary {
    border: 1px solid #327de8;
  }

  &.primary :deep(.el-sub-menu) {
    background-color: #327de8;
  }

  &.primary:hover .el-button,
  &.primary:hover :deep(.el-sub-menu) {
    background-color: #5b97ed;
  }

  &.primary:active .el-button,
  &.primary:active :deep(.el-sub-menu) {
    background-color: #2864b9;
  }

  &.primary :deep(.el-sub-menu__icon-arrow) {
    color: white !important;
  }

  &[link] {
    border: none;
    border-radius: 4px;
  }

  &[link] .el-button {
    border-right: none;
    background-color: transparent;
    color: #606266 !important;
    padding: 0 0 0 8px;
  }

  &[link]:hover {
    background-color: #f4f6f9 !important;
  }

  :deep(.el-sub-menu__title) {
    height: 30px !important;

    .el-icon {
      font-size: 14px !important;
    }
  }

  :deep(.el-sub-menu__title:hover) {
    background-color: transparent;
  }

  &:not(.no-button) {
    :deep(.el-sub-menu__title) {
      width: 28px !important;
      padding: 0 !important;
    }

    :deep(.el-sub-menu__icon-arrow) {
      right: 0 !important;
    }
  }

  &.no-button {
    :deep(.el-sub-menu__title) {
      padding: 2px 24px 2px 12px !important;
    }

    :deep(.el-sub-menu__icon-arrow) {
      right: 8px;
    }
  }

  &:hover :deep(.el-sub-menu__icon-arrow) {
    color: #2f7ff4;
  }

  &[link] :deep(.el-sub-menu__icon-arrow) {
    color: #606266 !important;
  }
}

.st-menu2 {
  height: 32px !important;
  min-width: max-content !important;
  --el-menu-base-level-padding: 12px;
  //color: #606266!important;

  :deep(.el-icon) {
    font-size: 14px;
    margin-right: 3px;
  }
}

.st-menu2.el-menu-item {
  --el-menu-hover-bg-color: white;
}

.st-menu2[text] {
  border: none !important;
  color: #327de8 !important;
  padding: 4px;
}

.st-menu2[text].is-active {
  color: #327de8 !important;
}

.st-menu2[text]:not(:first-child)::before {
  content: '';
  position: absolute;
  width: 1px;
  height: 14px;
  background-color: #327de8;
  top: 10px;
  left: -4px;
  cursor: default;
}

.st-menu2[link] {
  color: #606266 !important;
  border: none !important;
  padding: 0 8px;
}

.st-menu2[has-icon] {
  padding-left: 4px;
}

.st-menu2.el-menu-item[link]:hover {
  background-color: #f4f6f9 !important;
  color: #606266 !important;
}
</style>

<style lang="scss">
// 全局style, 控制popper的偏移
body {
  --st-sm-popper-offset: 100px;
  --st-sm-popper-minwidth: 100px;
}

.st-sm-popper {
  margin-left: var(--st-sm-popper-offset) !important;

  --el-menu-base-level-padding: 12px;
  --el-menu-horizontal-sub-item-height: 34px;
  --el-menu-hover-bg-color: #f3f4f6;

  padding: 1px;
  background-color: white;
  border: none !important;
  box-shadow: 0 0 6px #e4e7ed;

  .el-menu--popup {
    padding: 0;
    box-shadow: none;
    min-width: var(--st-sm-popper-minwidth);
  }

  .el-menu--popup-container {
    border: none;
  }

  .el-popper {
    box-shadow: 0 0 6px #e4e7ed;
  }
}
</style>
